import { useLayoutEffect, useState, type RefObject } from 'react';

/* ===========================================================================
 * WHERE A PORTALLED PANEL GOES — measured off its trigger, in the viewport.
 *
 * Extracted from `RowActions`, which is where the arithmetic was written and
 * where its reasons are recorded. It is a hook rather than a copied block for
 * the reason `FiltersPopover` demonstrates: that component needs the same
 * measurement, and the two versions of "which way does this open" would drift
 * the first time either was adjusted — at which point a menu and a filter panel
 * on the same toolbar would flip in different directions on the same screen.
 *
 * The three properties it exists to keep, all of which are invisible until they
 * break, and each of which broke at least once before this was centralised:
 *
 *  • VIEWPORT, NOT PARENT. Both callers open inside a Card that is
 *    `overflow-hidden rounded-card`. An absolutely-positioned panel is clipped
 *    by a box that knows nothing about it — and the row a menu hangs off is
 *    often the LAST one, so the clip lands on the item most likely to be
 *    Delete.
 *  • FLIP ONLY WHEN IT HELPS. Above is used only when below genuinely does not
 *    fit AND above is roomier. Flipping eagerly moves a panel a reader is
 *    already looking at.
 *  • TRAILING-ALIGNED, AND IT MIRRORS. The panel's far edge meets the
 *    trigger's far edge, so it opens back across the table rather than off the
 *    edge of it — and under Arabic the trailing edge is on the left, which is
 *    read off the trigger's own computed direction rather than assumed.
 *
 * `null` while closed, and while open but not yet measured. Callers park the
 * panel off-screen until it is non-null rather than flashing it at 0,0;
 * `useLayoutEffect` runs before paint, so that only ever shows if the trigger
 * has gone away.
 * ======================================================================== */

export interface AnchoredPosition {
  top: number;
  left: number;
  maxHeight: number;
  /** Set only in `stretch` mode: the trigger's width, for the panel to match. */
  width?: number;
}

export interface AnchoredPanelOptions {
  /**
   * STRETCH — the panel is the trigger's own width, aligned to its leading edge.
   *
   * The default (trailing-aligned, panel's natural width) is right for a MENU
   * hanging off a ⋯ button: it opens back across the table rather than off the
   * edge of it. It is wrong for a LISTBOX under a text input, where the list is
   * the input's dropdown and any other width or offset reads as a second,
   * misaligned control.
   *
   * Only the horizontal rule changes. The vertical half — how much room is
   * below, whether to flip above, what the max height is — is shared, and that
   * is the half that actually drifts: it is arithmetic nobody re-derives
   * correctly, and a menu and a combobox on one toolbar flipping in different
   * directions is the defect this hook was extracted to prevent.
   */
  stretch?: boolean;
}

export function useAnchoredPanel(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  options: AnchoredPanelOptions = {}
): AnchoredPosition | null {
  const { stretch = false } = options;
  const [pos, setPos] = useState<AnchoredPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    function place(): void {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const t = trigger.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      const GAP = 6;
      const EDGE = 8;
      const rtl = getComputedStyle(trigger).direction === 'rtl';

      const below = window.innerHeight - t.bottom - GAP - EDGE;
      const above = t.top - GAP - EDGE;
      const natural = panel.scrollHeight;
      /* Above only when below genuinely does not fit AND above is roomier. A
         menu on the last row of a long table is exactly this case, and it is
         why the flip exists: without it the one item a reader most needs to
         read carefully sits half off the bottom of the glass. */
      const useAbove = natural > below && above > below;
      const room = Math.max(96, useAbove ? above : below);
      const height = Math.min(natural, room);
      const top = useAbove
        ? Math.max(EDGE, t.top - GAP - height)
        : Math.min(t.bottom + GAP, Math.max(EDGE, window.innerHeight - EDGE - height));
      /* STRETCH: the list IS the input's dropdown, so it takes the input's
         width and its leading edge, and there is nothing to mirror — matching
         the trigger's box mirrors with it. */
      if (stretch) {
        const stretched = Math.min(
          Math.max(EDGE, t.left),
          Math.max(EDGE, window.innerWidth - t.width - EDGE)
        );
        commit({ top, left: stretched, maxHeight: room, width: t.width });
        return;
      }
      // Trailing-aligned: the panel's far edge meets the trigger's far edge, so
      // it opens back across the table rather than off the edge of it. Mirrors
      // under Arabic, where the trailing edge is on the left.
      let left = rtl ? t.left : t.right - p.width;
      left = Math.min(Math.max(EDGE, left), Math.max(EDGE, window.innerWidth - p.width - EDGE));
      commit({ top, left, maxHeight: room });
    }

    /* Same numbers in, no state change out. React would bail on an equal object
       only if it were the SAME object, and this one is fresh every pass — so the
       comparison is here, and it is what makes observing our own box safe. */
    function commit(next: AnchoredPosition): void {
      setPos((prev) =>
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.maxHeight === next.maxHeight &&
        prev.width === next.width
          ? prev
          : next
      );
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    /* ── AND WHEN THE PANEL ITSELF CHANGES SIZE ───────────────────────────────
     *
     * THE DEFECT. `place()` ran on open, on scroll and on window resize — never
     * on the panel's own content changing. A combobox opens with every option
     * in it, so the list is tall, so `useAbove` fires and the top is computed as
     * `trigger.top - gap - TALL_HEIGHT`. Then the reader types three letters,
     * the list drops from forty options to six, the panel shrinks — and its TOP
     * does not move. The list is left floating hundreds of pixels above the
     * field, over unrelated parts of the screen, which is exactly what the
     * owner reported: "why is the filter floating after writing something".
     *
     * A ResizeObserver rather than a dependency on the caller's filtered list:
     * the hook cannot see the caller's state, and asking every call site to
     * remember to pass its own length is the kind of contract that holds for
     * two components and breaks on the third. Size is the thing that actually
     * matters here, and the panel can report its own.
     *
     * `place` is idempotent for an unchanged layout — it setState's the same
     * numbers and React bails out of the re-render — so observing a box whose
     * size this effect can itself influence does not loop. */
    /* Guarded: this is a library, and an environment without ResizeObserver
       (jsdom, a very old browser) must lose the re-placement, never throw. The
       panel then behaves as it did before — correct on open, stale after the
       content changes — which is a degradation and not a crash. */
    const ro =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (ro && panelRef.current) ro.observe(panelRef.current);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      ro?.disconnect();
    };
  }, [open, triggerRef, panelRef, stretch]);

  return pos;
}
